/**
 * Apple Ads → 广告命名模板（CLAUDE.md §pages/<domain> 薄壳）。
 *
 * 镜像 pages/huawei/AdNameTemplatePage.tsx；编辑器主体见
 * components/adNameTemplate/AdNameTemplateEditor.tsx（跨平台共享）。
 * 与华为页的差异：
 *   - 一页两组模板：广告系列（campaign）与广告组（ad_group）各自独立编辑/保存，
 *     经路径参数 {group} 区分，共用同一张表 apple_asa_ad_name_template；
 *   - 两组使用相同的字段默认分隔符「-」（newEmptyTemplate 缺省即为 '-'）；
 *   - 空白模板不带任何默认字段；每个字段仅 1 组枚举（allowSecondCombo=false）。
 */

import { useCallback } from "react";
import { AdNameTemplateEditor } from "../../components/adNameTemplate/AdNameTemplateEditor";
import {
  getLatestTemplate,
  saveTemplate,
} from "../../services/apple/adNameTemplate";
import type { SaveAdNameTemplateBody } from "../../types/adNameTemplate.types";
import { newEmptyTemplate } from "../../utils/adNameTemplateFactory";
import { PAGE_KEYS } from "../../config/permissions";

/** 两组模板的区块说明（与页面顶部说明互补：每组各自覆盖历史版本） */
const GROUP_DESCRIPTION =
  "该组始终只保留唯一一个配置；任何修改保存后即覆盖历史版本。";

export default function AdNameTemplatePage() {
  // 注意：编辑器的 reload effect 依赖 fetchLatest / save 的引用稳定性，
  // 必须经 useCallback 绑定组标识后再注入，避免每次渲染生成新函数导致重复拉取。
  const fetchCampaign = useCallback(
    () => getLatestTemplate("campaign"),
    [],
  );
  const saveCampaign = useCallback(
    (body: SaveAdNameTemplateBody) => saveTemplate("campaign", body),
    [],
  );
  const fetchAdGroup = useCallback(() => getLatestTemplate("ad_group"), []);
  const saveAdGroup = useCallback(
    (body: SaveAdNameTemplateBody) => saveTemplate("ad_group", body),
    [],
  );

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      {/* 页面级标题：一页两组（广告系列 / 广告组），分隔符统一为「-」 */}
      <div className="mb-2">
        <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
          广告命名模板
        </h1>
        <p className="mt-1 text-[12.5px] text-text-muted">
          广告系列与广告组分别维护命名模板；两组字段默认分隔符统一为
          「-」，修改保存后即覆盖历史版本。
        </p>
      </div>

      {/* 广告系列（campaign）模板组（顶部与下方广告组区块同款分隔线，与页面说明拉开间距） */}
      <div className="mt-6 border-t border-line-subtle pt-8">
        <AdNameTemplateEditor
          pageKey={PAGE_KEYS.APPLE_AD_NAME_TEMPLATE}
          fetchLatest={fetchCampaign}
          save={saveCampaign}
          createBlankTemplate={newEmptyTemplate}
          allowSecondCombo={false}
          title="广告系列命名模板"
          description={GROUP_DESCRIPTION}
          embedded
        />
      </div>

      {/* 广告组（ad_group）模板组 */}
      <div className="mt-10 border-t border-line-subtle pt-8">
        <AdNameTemplateEditor
          pageKey={PAGE_KEYS.APPLE_AD_NAME_TEMPLATE}
          fetchLatest={fetchAdGroup}
          save={saveAdGroup}
          createBlankTemplate={newEmptyTemplate}
          allowSecondCombo={false}
          title="广告组命名模板"
          description={GROUP_DESCRIPTION}
          embedded
        />
      </div>
    </div>
  );
}
